import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import {
  CheckCircle2, Loader2, ShieldCheck, Stethoscope, Plane, Scale, FolderLock,
  Clock, AlertTriangle, Zap,
} from "lucide-react";

const StatusRow = ({ icon: Icon, label, value, tone = "slate", testid }) => (
  <div className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0" data-testid={testid}>
    <span className="flex items-center gap-2 text-sm text-slate-500"><Icon className="w-4 h-4 text-cobalt" /> {label}</span>
    <span className={`text-sm font-semibold text-right ${tone === "green" ? "text-emerald-600" : tone === "amber" ? "text-amber-600" : "text-navy"}`}>{value}</span>
  </div>
);

export default function SubmissionConfirmation() {
  const { caseId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/cases/${caseId}/routing-status`).then(({ data }) => setData(data)).catch(() => setError(true));
  }, [caseId]);

  if (error) return <AppLayout><div className="py-20 text-center text-slate-500">Could not load submission status. <Link to={`/cases/${caseId}`} className="text-cobalt font-medium">Open your case</Link></div></AppLayout>;
  if (!data) return <AppLayout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div></AppLayout>;

  const blocked = data.routing_status === "verification_hold" || data.routing_status === "manual_review_required" || (data.routing_missing || []).length > 0;
  const priority = data.rear_end_priority && !blocked;
  const marketplace = data.routing_status === "released_to_marketplace";

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto" data-testid="submission-confirmation">
        <div className="text-center mb-8">
          <div className={`h-16 w-16 rounded-full mx-auto flex items-center justify-center ${blocked ? "bg-amber-50" : "bg-emerald-50"}`}>
            {blocked ? <AlertTriangle className="w-8 h-8 text-amber-500" /> : <CheckCircle2 className="w-8 h-8 text-emerald-500" />}
          </div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-navy mt-4" data-testid="confirmation-headline">
            {blocked
              ? "Your accident report has been saved"
              : "Your Crash Ready case has been completed and securely submitted for attorney review."}
          </h1>
          {blocked && (
            <p className="mt-3 text-slate-600 text-sm" data-testid="blocked-message">
              Your accident report has been saved, but it cannot be submitted for attorney review until the remaining verification requirements are completed.
            </p>
          )}
          {priority && data.routing_status !== "verification_hold" && (
            <p className="mt-3 text-slate-600 text-sm" data-testid="priority-message">
              Because this appears to be a verified rear-end accident with other-driver insurance, eligible participating attorneys may be notified immediately.
            </p>
          )}
          {marketplace && !priority && (
            <p className="mt-3 text-slate-600 text-sm" data-testid="marketplace-message">
              Your verified case has been placed in the Crash Ready Attorney Marketplace for review by participating attorneys.
            </p>
          )}
          {!blocked && (
            <p className="mt-2 text-xs text-slate-400">Submitting your information does not guarantee attorney acceptance or create an attorney-client relationship.</p>
          )}
        </div>

        {priority && (
          <div className="mb-4 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-center gap-2 text-amber-700 text-xs font-bold uppercase tracking-wide" data-testid="rear-end-priority-banner">
            <Zap className="w-4 h-4" /> {data.rear_end_label}
          </div>
        )}

        <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <StatusRow icon={ShieldCheck} label="Case ID" value={data.case_number || data.case_id} testid="conf-case-id" />
          <StatusRow icon={Clock} label="Submission time" value={data.submitted_at ? new Date(data.submitted_at).toLocaleString() : "—"} testid="conf-submitted-at" />
          <StatusRow icon={ShieldCheck} label="Verification status"
            value={data.verification?.trust_class ? `${data.verification.trust_class} (${data.verification.trust_score})` : "Standard"}
            tone={data.verification?.on_hold ? "amber" : "green"} testid="conf-verification" />
          <StatusRow icon={Stethoscope} label="RN status"
            value={data.rn_status?.case_verified ? "RN Case Verified" : (data.rn_status?.intake_status ? `Intake ${data.rn_status.intake_status}` : "Not requested")}
            tone={data.rn_status?.case_verified ? "green" : "slate"} testid="conf-rn" />
          <StatusRow icon={Plane} label="Drone status"
            value={data.drone_status?.approved ? "Approved" : (data.drone_status?.decision || "Not requested")}
            tone={data.drone_status?.approved ? "green" : "slate"} testid="conf-drone" />
          <StatusRow icon={Scale} label="Attorney routing"
            value={data.routing_label || "Not submitted"}
            tone={blocked ? "amber" : "green"} testid="conf-routing" />
          <StatusRow icon={FolderLock} label="Evidence Locker"
            value={`${data.evidence_locker?.permanent ?? 0} permanent · ${data.evidence_locker?.temporary ?? 0} temporary`}
            testid="conf-evidence" />
        </div>

        {(data.routing_missing || []).length > 0 && (
          <div className="mt-4 bg-white rounded-3xl border border-amber-200 p-6" data-testid="missing-requirements">
            <h3 className="font-heading font-semibold text-navy text-sm mb-3">Remaining requirements</h3>
            <ul className="space-y-2">
              {data.routing_missing.map((m, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-600" data-testid={`missing-item-${i}`}>
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> {m}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6 flex gap-3 justify-center">
          <Link to={`/cases/${caseId}`} data-testid="view-case-btn" className="px-6 py-3 rounded-full bg-cobalt text-white font-medium hover:bg-cobalt-hover transition-all shadow-md">View my case</Link>
          <Link to="/dashboard" data-testid="back-dashboard-btn" className="px-6 py-3 rounded-full bg-white border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition-all">Dashboard</Link>
        </div>
      </div>
    </AppLayout>
  );
}
